'use client'

/* Renders OFFICIAL Instagram / X post embeds for a shaheed memorial record. We NEVER download or
   re-host the image — the platform's own embed loads the media from the platform. Each embed:
   • lazy-loads (the platform script + iframe are only fetched once the card scrolls into view),
   • always shows a plain "View on Instagram/X" link that doubles as the fallback if the embed is
     blocked or fails to render,
   • shows the admin-entered credit line beneath.
   Requires the Instagram/X hosts in the CSP script-src + frame-src (see next.config.js). */

import { useEffect, useRef, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import type { ShaheedEmbed } from '../../../../lib/shaheed'

const SCRIPTS: Record<ShaheedEmbed['platform'], string> = {
  instagram: 'https://www.instagram.com/embed.js',
  x: 'https://platform.twitter.com/widgets.js',
}
const LABEL: Record<ShaheedEmbed['platform'], string> = { instagram: 'Instagram', x: 'X' }

// Load each platform script at most once; resolve when ready (reject on error → fallback link stands).
const scriptCache = new Map<string, Promise<void>>()
function loadScript(src: string): Promise<void> {
  const cached = scriptCache.get(src)
  if (cached) return cached
  const p = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`)
    if (existing) {
      if ((existing as { _loaded?: boolean })._loaded) { resolve(); return }
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('script error')))
      return
    }
    const s = document.createElement('script')
    s.src = src; s.async = true
    s.addEventListener('load', () => { (s as { _loaded?: boolean })._loaded = true; resolve() })
    s.addEventListener('error', () => reject(new Error('script error')))
    document.body.appendChild(s)
  })
  scriptCache.set(src, p)
  return p
}

function EmbedCard({ embed }: { embed: ShaheedEmbed }) {
  const ref = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(false)

  // Lazy: only activate once the card is near the viewport.
  useEffect(() => {
    const el = ref.current
    if (!el || active) return
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setActive(true); io.disconnect() }
    }, { rootMargin: '300px' })
    io.observe(el)
    return () => io.disconnect()
  }, [active])

  // Once active, load the platform script and let it transform the blockquote.
  useEffect(() => {
    if (!active) return
    let cancelled = false
    loadScript(SCRIPTS[embed.platform]).then(() => {
      if (cancelled) return
      const w = window as any
      if (embed.platform === 'instagram') w.instgrm?.Embeds?.process?.()
      else w.twttr?.widgets?.load?.(ref.current)
    }).catch(() => { /* fallback link already visible */ })
    return () => { cancelled = true }
  }, [active, embed.platform])

  return (
    <div className="shd-embed" ref={ref}>
      {active && (
        embed.platform === 'instagram' ? (
          <blockquote
            className="instagram-media"
            data-instgrm-permalink={embed.url}
            data-instgrm-version="14"
            style={{ margin: 0, width: '100%', maxWidth: 540 }}
          />
        ) : (
          <blockquote className="twitter-tweet" data-dnt="true" data-theme="dark">
            <a href={embed.url}>{embed.url}</a>
          </blockquote>
        )
      )}
      <a className="shd-embed-link" href={embed.url} target="_blank" rel="noopener noreferrer">
        View on {LABEL[embed.platform]} <ExternalLink size={11} />
      </a>
      {embed.credit && <p className="shd-embed-credit">{embed.credit}</p>}
    </div>
  )
}

export default function ShaheedEmbeds({ embeds }: { embeds: ShaheedEmbed[] }) {
  if (!embeds.length) return null
  return (
    <div className="shd-embeds">
      {embeds.map((e, i) => <EmbedCard key={`${e.platform}-${i}`} embed={e} />)}
    </div>
  )
}
